import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Edit2, Trash2, Mail, Phone, Building, Briefcase, Camera, Upload, X, Loader2 } from 'lucide-react';
import { extractBusinessCardInfo } from '../services/geminiService';

export default function BusinessCards() {
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const [cards, setCards] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [cdSearchTerm, setCdSearchTerm] = useState('');
  const [showCdDropdown, setShowCdDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);

  // Form State
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    job_title: '',
    company: '',
    phone: '',
    email: '',
    address: '',
    cd_no: '',
    team_name: user?.team_name || ''
  });

  useEffect(() => {
    fetchCards();
    fetchAllCustomers();

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCdDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCards = async () => {
    setIsLoading(true);
    try {
      const email = user?.google_email || '';
      const role = user?.role || 'sale';
      const team_name = user?.team_name || '';
      const url = `/api/business_cards?email=${email}&role=${role}&team_name=${team_name}`;
      
      const res = await fetch(url);
      const data = await res.json();
      if (data.status === 'success') {
        setCards(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch cards", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllCustomers = async () => {
    try {
      const res = await fetch('/api/customers');
      const data = await res.json();
      if (data.status === 'success') {
        setAllCustomers(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch all customers", err);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Convert to Base64
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result;
      setPreviewImage(base64String);
      setIsExtracting(true);

      try {
        const extractedData = await extractBusinessCardInfo(base64String);
        
        // Auto-matching logic
        let matchedCdNo = '';
        let matchedCdText = '';
        
        if (extractedData.company) {
          const companyStr = extractedData.company.toLowerCase().trim();
          const match = allCustomers.find(c => 
            c.search_name?.toLowerCase().includes(companyStr) || 
            companyStr.includes(c.search_name?.toLowerCase())
          );
          if (match) {
            matchedCdNo = match.cd_no;
            matchedCdText = `${match.cd_no} - ${match.search_name}`;
          }
        }
        setCdSearchTerm(matchedCdText);

        setFormData(prev => ({
          ...prev,
          name: extractedData.name || '',
          job_title: extractedData.job_title || '',
          company: extractedData.company || '',
          phone: extractedData.phone || '',
          email: extractedData.email || '',
          address: extractedData.address || '',
          cd_no: matchedCdNo,
          raw_text: JSON.stringify(extractedData) // Keep as backup
        }));
      } catch (err) {
        alert("เกิดข้อผิดพลาดในการอ่านข้อมูล: " + err.message);
      } finally {
        setIsExtracting(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        image_url: previewImage, // Saving base64 directly to database
        created_by: user?.google_email
      };

      const res = await fetch('/api/business_cards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.status === 'success') {
        setShowModal(false);
        resetForm();
        fetchCards();
      } else {
        alert("Failed to save: " + data.message);
      }
    } catch (err) {
      alert("Error saving data");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("คุณต้องการลบนามบัตรนี้ใช่หรือไม่?")) return;
    try {
      const res = await fetch('/api/business_cards/' + id, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchCards();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditClick = (card) => {
    setFormData({
      id: card.id,
      name: card.name || '',
      job_title: card.job_title || '',
      company: card.company || '',
      phone: card.phone || '',
      email: card.email || '',
      address: card.address || '',
      cd_no: card.cd_no || '',
      team_name: card.team_name || ''
    });
    setPreviewImage(card.image_url);
    
    if (card.cd_no) {
      const matchedCustomer = allCustomers.find(c => c.cd_no === card.cd_no);
      if (matchedCustomer) {
        setCdSearchTerm(`${matchedCustomer.cd_no} - ${matchedCustomer.search_name}`);
      } else {
        setCdSearchTerm(card.cd_no);
      }
    } else {
      setCdSearchTerm('');
    }
    
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      id: null,
      name: '',
      job_title: '',
      company: '',
      phone: '',
      email: '',
      address: '',
      cd_no: '',
      team_name: user?.team_name || ''
    });
    setPreviewImage(null);
    setCdSearchTerm('');
    setShowCdDropdown(false);
  };

  const filteredCards = cards.filter(card => {
    const matchedCustomer = allCustomers.find(c => c.cd_no === card.cd_no);
    const mappedCustomerName = matchedCustomer ? matchedCustomer.search_name : '';
    const term = searchTerm.toLowerCase();
    
    return (
      card.name?.toLowerCase().includes(term) || 
      card.company?.toLowerCase().includes(term) ||
      mappedCustomerName.toLowerCase().includes(term) ||
      card.cd_no?.toLowerCase().includes(term)
    );
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '1.5rem', paddingBottom: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, color: 'var(--color-text-main)', fontSize: '1.5rem' }}>นามบัตรลูกค้า</h2>
        <div style={{ position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input 
            type="text" 
            placeholder="ค้นหาชื่อ หรือ บริษัท..." 
            className="input-field"
            style={{ paddingLeft: '2.5rem', width: '250px' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Loader2 className="animate-spin" size={32} color="var(--color-primary)" />
        </div>
      ) : filteredCards.length === 0 ? (
        <div className="card" style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
          <Briefcase size={48} style={{ margin: '0 auto 1rem auto', opacity: 0.5 }} />
          <h3>ยังไม่มีนามบัตรในระบบ</h3>
          <p>กดปุ่มเพิ่มนามบัตรเพื่อเริ่มต้นบันทึกข้อมูล</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
          {filteredCards.map((card) => (
            <div key={card.id} className="card" style={{ overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column' }}>
              <div 
                style={{ height: '140px', backgroundColor: '#f1f5f9', position: 'relative', borderBottom: '1px solid #e2e8f0', backgroundImage: card.image_url ? `url(${card.image_url})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', cursor: card.image_url ? 'pointer' : 'default' }}
                onClick={() => { if (card.image_url) setSelectedImage(card.image_url); }}
              >
                {!card.image_url && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}>
                    <Briefcase size={48} />
                  </div>
                )}
                <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                  <button onClick={(e) => { e.stopPropagation(); handleEditClick(card); }} style={{ background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#3b82f6', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <Edit2 size={16} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(card.id); }} style={{ background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#ef4444', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <Trash2 size={16} />
                  </button>
                </div>
                {(user.role === 'admin' || user.role === 'super_admin') && (
                   <div style={{ position: 'absolute', bottom: '0.5rem', left: '0.5rem', background: 'rgba(0,0,0,0.6)', color: 'white', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                     Team: {card.team_name}
                   </div>
                )}
                {card.cd_no && (
                   <div style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', background: 'var(--color-primary)', color: 'white', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                     CD No: {card.cd_no}
                   </div>
                )}
              </div>
              <div style={{ padding: '1.25rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>{card.name || 'ไม่ระบุชื่อ'}</h3>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', fontWeight: 500 }}>{card.job_title || 'ไม่ระบุตำแหน่ง'}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#475569', marginTop: '0.5rem' }}>
                  <Building size={14} /> {card.company || 'ไม่ระบุบริษัท'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid #f1f5f9' }}>
                  {card.address && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.85rem', color: '#475569', wordBreak: 'break-all' }}>
                      <Building size={14} style={{ flexShrink: 0, marginTop: '2px' }} /> {card.address}
                    </div>
                  )}
                  {card.phone && (
                    <a href={`tel:${card.phone.replace(/[^0-9+]/g, '')}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#2563eb', textDecoration: 'none', wordBreak: 'break-all' }}>
                      <Phone size={14} style={{ flexShrink: 0 }} /> {card.phone}
                    </a>
                  )}
                  {card.email && (
                    <a href={`mailto:${card.email}`} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#2563eb', textDecoration: 'none', wordBreak: 'break-all' }}>
                      <Mail size={14} style={{ flexShrink: 0 }} /> {card.email}
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Floating Action Button */}
      <button 
        onClick={() => { resetForm(); setShowModal(true); }}
        style={{ position: 'fixed', bottom: '2rem', right: '2rem', backgroundColor: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: '50%', width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)', zIndex: 100 }}
      >
        <Plus size={28} />
      </button>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#0f172a' }}>เพิ่มนามบัตรใหม่</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={24} /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {/* Upload Section */}
              <div 
                style={{ border: '2px dashed #cbd5e1', borderRadius: '8px', padding: '2rem', textAlign: 'center', cursor: 'pointer', backgroundColor: '#f8fafc', position: 'relative' }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input 
                  type="file" 
                  accept="image/*" 
                  capture="environment"
                  ref={fileInputRef} 
                  style={{ display: 'none' }} 
                  onChange={handleImageUpload}
                />
                
                {isExtracting ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', color: '#3b82f6' }}>
                    <Loader2 className="animate-spin" size={32} />
                    <span>AI กำลังอ่านนามบัตร...</span>
                  </div>
                ) : previewImage ? (
                  <img src={previewImage} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '4px' }} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', color: '#64748b' }}>
                    <Camera size={48} />
                    <span>คลิกเพื่อถ่ายรูปหรืออัปโหลดนามบัตร</span>
                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>ระบบจะอ่านข้อมูลให้อัตโนมัติ</span>
                  </div>
                )}
              </div>

              {/* Form Section */}
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: '#475569', fontWeight: 500 }}>ชื่อ - นามสกุล</label>
                  <input type="text" className="input-field" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} style={{ width: '100%' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: '#475569', fontWeight: 500 }}>ตำแหน่ง</label>
                  <input type="text" className="input-field" value={formData.job_title} onChange={e => setFormData({...formData, job_title: e.target.value})} style={{ width: '100%' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: '#475569', fontWeight: 500 }}>บริษัท / องค์กร</label>
                  <input type="text" className="input-field" value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} style={{ width: '100%' }} />
                </div>
                <div style={{ position: 'relative' }} ref={dropdownRef}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: '#475569', fontWeight: 500 }}>ผูกกับลูกค้าในระบบ (CD No.)</label>
                  <input 
                    type="text" 
                    className="input-field" 
                    placeholder="พิมพ์อย่างน้อย 3 ตัวเพื่อค้นหา..."
                    value={cdSearchTerm}
                    onChange={(e) => {
                      setCdSearchTerm(e.target.value);
                      if (e.target.value === '') setFormData({...formData, cd_no: ''});
                      setShowCdDropdown(e.target.value.length >= 3);
                    }}
                    onFocus={() => {
                      if (cdSearchTerm.length >= 3) setShowCdDropdown(true);
                    }}
                    style={{ width: '100%' }}
                  />
                  {showCdDropdown && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '4px', maxHeight: '200px', overflowY: 'auto', zIndex: 10, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                      {allCustomers.filter(c => 
                        c.search_name?.toLowerCase().includes(cdSearchTerm.toLowerCase()) || 
                        c.cd_no?.toLowerCase().includes(cdSearchTerm.toLowerCase())
                      ).length > 0 ? (
                        allCustomers.filter(c => 
                          c.search_name?.toLowerCase().includes(cdSearchTerm.toLowerCase()) || 
                          c.cd_no?.toLowerCase().includes(cdSearchTerm.toLowerCase())
                        ).map(c => (
                          <div 
                            key={c.cd_no} 
                            style={{ padding: '0.5rem 1rem', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' }}
                            onClick={() => {
                              setFormData({...formData, cd_no: c.cd_no});
                              setCdSearchTerm(`${c.cd_no} - ${c.search_name}`);
                              setShowCdDropdown(false);
                            }}
                            onMouseEnter={e => e.target.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={e => e.target.style.backgroundColor = 'white'}
                          >
                            {c.cd_no} - {c.search_name}
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: '0.5rem 1rem', color: '#94a3b8', fontSize: '0.85rem' }}>ไม่พบข้อมูลลูกค้าที่ตรงกัน</div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: '#475569', fontWeight: 500 }}>เบอร์โทรศัพท์</label>
                    <input type="text" className="input-field" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} style={{ width: '100%' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: '#475569', fontWeight: 500 }}>อีเมล</label>
                    <input type="email" className="input-field" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} style={{ width: '100%' }} />
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', color: '#475569', fontWeight: 500 }}>ที่อยู่</label>
                  <textarea 
                    className="input-field" 
                    value={formData.address} 
                    onChange={e => setFormData({...formData, address: e.target.value})} 
                    style={{ width: '100%', minHeight: '60px', resize: 'vertical' }} 
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
              <button onClick={() => setShowModal(false)} className="btn-secondary">ยกเลิก</button>
              <button onClick={handleSave} className="btn-primary" disabled={isSaving || isExtracting}>
                {isSaving ? <><Loader2 className="animate-spin" size={16} /> กำลังบันทึก...</> : 'บันทึกข้อมูล'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Image Viewer Modal */}
      {selectedImage && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }} onClick={() => setSelectedImage(null)}>
          <button 
            style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '0.5rem' }}
            onClick={(e) => { e.stopPropagation(); setSelectedImage(null); }}
          >
            <X size={32} />
          </button>
          <img 
            src={selectedImage} 
            alt="Full Business Card" 
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.5)' }} 
            onClick={(e) => e.stopPropagation()} 
          />
        </div>
      )}
    </div>
  );
}
